import { SignUpModal } from '../../../shared';
import React, { useContext, useState } from 'react';
import { GlobalSiteDataContext } from '../../../../context';
import { DynamicHeroContent } from './DynamicHeroContent';
import { useBuilderImages } from '../../context/BuilderImageContext';
import { useOptimizedImage } from '../../../../hooks/useOptimizedImage';
import { GatsbyImage } from 'gatsby-plugin-image';
import './HeroSections.styles.css';

const styles = {
    image: {
        objectFit: 'cover',
        objectPosition: 'right center',
        width: '100%',
        height: '100%',
    },

    backgrounds: {
        section: (color) => ({ backgroundColor: color }),
        content: (color) => ({ backgroundColor: color }),
    },
};

const commonClasses = {
    imageContainer: 'h-100 position-relative',
    contentWrapper: 'd-flex flex-column justify-content-center h-100',
};

export function DynamicHeroBuilderV2({
    heroDesktopImage,
    heroTabletImage,
    heroMobileImage,
    heroMobileImageAlt,
    typefaceLogo,
    heading,
    headingColor,
    headingColorMobileAndTablet,
    disclaimerColor,
    disclaimerColorMobileAndTablet,
    disclaimerLinkText,
    disclaimerTextPromise,
    buttonLink,
    buttonLabel,
    buttonVariant = 'primary',
    richTextContentMobileAndTablet,
    richTextContentDesktop,
    isHeroImgOnRight,
    isButtonCentered = true,
    footerText,
    logoImageAlt,
    desktopImageBackgroundColor,
    desktopBackgroundColor,
    contentBackgroundColor,
    footerTextColor,
    footerTextColorMobileAndTablet,
}) {
    const siteData = useContext(GlobalSiteDataContext);
    const [showSignUpModal, setShowSignUpModal] = useState(false);
    const { getOptimizedImage } = useBuilderImages();

    // Get optimized image data for each breakpoint
    const desktopImageInfo = heroDesktopImage ? getOptimizedImage(heroDesktopImage) : null;
    const tabletImageInfo = heroTabletImage ? getOptimizedImage(heroTabletImage) : null;
    const mobileImageInfo = heroMobileImage ? getOptimizedImage(heroMobileImage) : null;

    const { gatsbyImageData: desktopGatsbyImage } = useOptimizedImage(
        desktopImageInfo?.nodeId || '',
        heroMobileImageAlt || 'Hero image'
    );
    const { gatsbyImageData: tabletGatsbyImage } = useOptimizedImage(
        tabletImageInfo?.nodeId || '',
        heroMobileImageAlt || 'Hero image'
    );
    const { gatsbyImageData: mobileGatsbyImage, altText } = useOptimizedImage(
        mobileImageInfo?.nodeId || '',
        heroMobileImageAlt || 'Hero image'
    );

    return (
        // Main container with desktop background color
        <section
            className="position-relative overflow-hidden"
            style={styles.backgrounds.section(desktopImageBackgroundColor)}
        >
            {/* Modal Component */}
            {showSignUpModal && (
                <SignUpModal
                    siteData={siteData}
                    showSignUpModal={showSignUpModal}
                    setShowSignUpModal={setShowSignUpModal}
                />
            )}

            {/* Main content container using Bootstrap grid */}
            <div className="container-fluid px-0">
                <div className={`row ${isHeroImgOnRight ? 'flex-row-reverse' : ''}`}>
                    {/* Image Column */}
                    <div className="col-12 col-lg-6 px-0">
                        <div className={commonClasses.imageContainer}>
                            <div className="d-none d-lg-block h-100 hero-image-container">
                                {desktopGatsbyImage ? (
                                    <GatsbyImage
                                        image={desktopGatsbyImage}
                                        alt={altText}
                                        style={styles.image}
                                        className="img-fluid"
                                    />
                                ) : (
                                    <img
                                        src={heroDesktopImage}
                                        alt={heroMobileImageAlt}
                                        style={styles.image}
                                        className="img-fluid"
                                        loading="eager"
                                        decoding="async"
                                    />
                                )}
                            </div>

                            {/* Tablet - show only on md to lg */}
                            <div className="d-none d-md-block d-lg-none h-100">
                                {tabletGatsbyImage ? (
                                    <GatsbyImage
                                        image={tabletGatsbyImage}
                                        alt={altText}
                                        style={styles.image}
                                        className="img-fluid"
                                    />
                                ) : (
                                    <img
                                        src={heroTabletImage}
                                        alt={heroMobileImageAlt}
                                        style={styles.image}
                                        className="img-fluid"
                                        loading="eager"
                                        decoding="async"
                                    />
                                )}
                            </div>

                            {/* Mobile - show only on < md */}
                            <div className="d-block d-md-none h-100">
                                {mobileGatsbyImage ? (
                                    <GatsbyImage
                                        image={mobileGatsbyImage}
                                        alt={altText}
                                        style={styles.image}
                                        className="img-fluid"
                                    />
                                ) : (
                                    <img
                                        src={heroMobileImage}
                                        alt={heroMobileImageAlt}
                                        style={styles.image}
                                        className="img-fluid"
                                        loading="eager"
                                        decoding="async"
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Content Column */}
                    <div className="col-12 col-lg-6" style={styles.backgrounds.content(desktopBackgroundColor)}>
                        {/* Desktop Content with right padding */}
                        <div className="d-none d-lg-block h-100">
                            <div className="container h-100">
                                <div className="row h-100 pt-xxl-0">
                                    <div className="col-lg-12 col-xxl-9 col-xxxl-9 h-100 pe-xxl-5">
                                        <div className={`${commonClasses.contentWrapper} py-5`}>
                                            <DynamicHeroContent
                                                {...{
                                                    buttonLabel,
                                                    buttonLink,
                                                    buttonVariant,
                                                    heading,
                                                    headingColor,
                                                    disclaimerLinkText,
                                                    disclaimerTextPromise,
                                                    disclaimerColor,
                                                    logoImageAlt,
                                                    richTextContent: richTextContentDesktop,
                                                    setShowSignUpModal,
                                                    typefaceLogo,
                                                    footerText,
                                                    footerTextColor,
                                                    isButtonCentered,
                                                    isDesktop: true,
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Mobile/Tablet Content */}
                        <div
                            className="d-block d-lg-none h-100"
                            style={styles.backgrounds.content(contentBackgroundColor)}
                        >
                            <div
                                className={`${commonClasses.contentWrapper} container milan-py`}
                                style={{ paddingTop: '1rem' }}
                            >
                                <DynamicHeroContent
                                    {...{
                                        buttonLabel,
                                        buttonLink,
                                        buttonVariant,
                                        heading,
                                        headingColor: headingColorMobileAndTablet,
                                        disclaimerLinkText,
                                        disclaimerTextPromise,
                                        disclaimerColor: disclaimerColorMobileAndTablet,
                                        logoImageAlt,
                                        richTextContent: richTextContentMobileAndTablet,
                                        setShowSignUpModal,
                                        typefaceLogo,
                                        footerText,
                                        footerTextColor: footerTextColorMobileAndTablet,
                                        isButtonCentered,
                                        isDesktop: false,
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
